<template>
  <div>
    <pa-admin-toolbar
      :show-per-page="false"
      :search.sync="searchInput"
      :from.sync="fromInput"
      :to.sync="toInput"
      :busy="loading"
      placeholder="Keyword, email or user ID (optional)"
      @search="load"
    />
    <div class="paa-filters">
      <pa-admin-filter label="Provider" :value="provider" :options="providerOptions" @input="setProvider" />
    </div>

    <div v-if="loadError" class="paa-error">{{ loadError }}</div>

    <div class="paa-card sv-intro">
      This tab does not change anything. It replays the stored pages against other thresholds so we can see,
      before touching the code, how many pages each value would call poisoned and how that matches the
      Healthy / Poisoned verdicts given in the History tab. Today a page is poisoned below
      {{ current.failBelow }} and fine from {{ current.okFrom }}; thresholds are changed in the code.
    </div>

    <div class="paa-stat-row">
      <span class="paa-muted">
        {{ totals.pages || 0 }} stored pages ({{ totals.page1 || 0 }} page 1, {{ totals.page2 || 0 }} page 2),
        verdicts: {{ totals.verdictPoisoned || 0 }} poisoned, {{ totals.verdictHealthy || 0 }} healthy.
      </span>
    </div>

    <div class="paa-card paa-tblwrap">
      <table class="paa-tbl">
        <thead>
          <tr>
            <th><sv-tip label="Threshold" :lines="tips.threshold" /></th>
            <th class="paa-r"><sv-tip label="Page 1 below" :lines="tips.page1Below" /></th>
            <th class="paa-r"><sv-tip label="Page 2 below" :lines="tips.page2Below" /></th>
            <th class="paa-r"><sv-tip label="Poisoned (verdict) below" :lines="tips.poisonedBelow" /></th>
            <th class="paa-r"><sv-tip label="Healthy (verdict) below" :lines="tips.healthyBelow" /></th>
            <th class="paa-r"><sv-tip label="Poisoned (verdict) at or above" :lines="tips.poisonedAtOrAbove" /></th>
            <th class="paa-r"><sv-tip label="Healthy (verdict) at or above" :lines="tips.healthyAtOrAbove" /></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in thresholds" :key="row.threshold" :class="{ 'paa-sorted': isCurrent(row.threshold) }">
            <td>{{ row.threshold.toFixed(2) }}<span v-if="isCurrent(row.threshold)" class="paa-sub">in use</span></td>
            <td class="paa-r">{{ row.page1Below }}</td>
            <td class="paa-r">{{ row.page2Below }}</td>
            <td class="paa-r">{{ row.poisonedBelow }}</td>
            <td class="paa-r">{{ row.healthyBelow }}</td>
            <td class="paa-r">{{ row.poisonedAtOrAbove }}</td>
            <td class="paa-r">{{ row.healthyAtOrAbove }}</td>
          </tr>
          <tr v-if="!thresholds.length">
            <td class="paa-empty" colspan="7">{{ loading ? 'Loading…' : 'No stored page in this period.' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="paa-card paa-tblwrap" style="margin-top: 12px">
      <table class="paa-tbl">
        <thead>
          <tr>
            <th><sv-tip label="Score" :lines="tips.score" /></th>
            <th><sv-tip label="Page 1" :lines="tips.histPage1" /></th>
            <th><sv-tip label="Page 2" :lines="tips.histPage2" /></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="bucket in histogram" :key="bucket.bucket">
            <td>{{ bucket.bucket.toFixed(2) }} to {{ (bucket.bucket + 0.05).toFixed(2) }}</td>
            <td><span class="sv-bar" :style="{ width: barWidth(bucket.page1) }"></span> {{ bucket.page1 }}</td>
            <td><span class="sv-bar sv-bar--p2" :style="{ width: barWidth(bucket.page2) }"></span> {{ bucket.page2 }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import PaAdminFilter from '../PopAuthority/PaAdminFilter.vue'
import PaAdminToolbar from '../PopAuthority/PaAdminToolbar.vue'
import SvTip from './SvTip.vue'
import { SV_PROVIDER_OPTIONS, SV_TUNING_TIPS } from './serpValidationConstants'

const DATE_FORMAT = 'YYYY-MM-DD'

/** Tuning: how many stored pages each threshold would flag in the period, split by manual verdict. */
export default {
  name: 'AdminSerpValidationTuning',
  components: { PaAdminFilter, PaAdminToolbar, SvTip },
  data () {
    return {
      loading: false,
      loadError: '',
      searchInput: '',
      fromInput: moment().subtract(31, 'days').format(DATE_FORMAT),
      toInput: moment().format(DATE_FORMAT),
      provider: 'all',
      providerOptions: SV_PROVIDER_OPTIONS,
      tips: SV_TUNING_TIPS,
      thresholds: [],
      histogram: [],
      totals: {},
      current: { failBelow: 0.5, okFrom: 0.65 },
    }
  },
  computed: {
    maxBucket () {
      return Math.max(1, ...this.histogram.map((b) => Math.max(b.page1, b.page2)))
    },
  },
  created () {
    this.load()
  },
  methods: {
    isCurrent (threshold) {
      return Math.abs(threshold - this.current.failBelow) < 0.001 || Math.abs(threshold - this.current.okFrom) < 0.001
    },
    barWidth (count) {
      return Math.round((count / this.maxBucket) * 200) + 'px'
    },
    setProvider (value) {
      this.provider = value
      this.load()
    },
    params () {
      const params = new URLSearchParams()
      params.set('provider', this.provider)
      if (this.searchInput.trim()) params.set('filterText', this.searchInput.trim())
      if (this.fromInput) params.set('startDate', moment(this.fromInput, DATE_FORMAT).startOf('day').valueOf())
      if (this.toInput) params.set('endDate', moment(this.toInput, DATE_FORMAT).endOf('day').valueOf())
      return params.toString()
    },
    load () {
      this.loading = true
      this.loadError = ''
      return this.$store.dispatch('getSerpValidationTuningForAdmin', { params: this.params() })
        .then((response) => {
          const data = response && response.data
          if (!data || data.status !== 'SUCCESS') throw new Error((data && data.msg) || 'load failed')
          this.thresholds = data.thresholds || []
          this.histogram = data.histogram || []
          this.totals = data.totals || {}
          this.current = data.current || this.current
        })
        .catch((error) => {
          this.thresholds = []
          this.histogram = []
          this.loadError = (error && error.response && error.response.data && error.response.data.msg) ||
            'We could not load the tuning data. Try again in a moment.'
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>
